import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePurchaseOrders } from '../../../hooks/usePurchaseOrders'
import { fmtBRL } from '../../../lib/format'
import type { ProductRow, PurchaseOrderWithItems } from '../../../types'

// Quadro kanban ÚNICO do Painel K: junta o ciclo do PRODUTO (kanban_status:
// Analisar / Compra Aprovada) com o ciclo do PEDIDO (status: Pendente / Enviado /
// Recebido) numa única esteira ponta-a-ponta. Cada etapa é um card TOUCH que
// abre a lista dos seus itens. Card de pedido → abre Pedidos.
const COLS = [
  { key: 'analisar', label: 'Analisar',    accent: '#FF8A00', kind: 'prod' },
  { key: 'aprovado', label: 'Aprovado',    accent: '#16A34A', kind: 'prod' },
  { key: 'emitido',  label: 'Emitido',     accent: '#CA8A04', kind: 'order' },
  { key: 'transito', label: 'Em trânsito', accent: '#2563EB', kind: 'order' },
  { key: 'recebido', label: 'Recebido',    accent: '#0D9488', kind: 'order' },
] as const

interface KCard { id: string; title: string; sub: string; value: string }

const fmtInt = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 0 })
function fmtDate(iso: string | null): string {
  const m = (iso ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[3]}/${m[2]}` : '—'
}
const ACTIONABLE = new Set(['Zerado', 'Baixo', 'Comprar'])

export default function PainelKanban({ rows }: { rows: ProductRow[] }) {
  const navigate = useNavigate()
  const { orders } = usePurchaseOrders()
  // Começa com a 1ª etapa aberta; as demais o usuário abre tocando.
  const [open, setOpen] = useState<Set<string>>(() => new Set(['analisar']))
  const toggle = (k: string) => setOpen(prev => {
    const n = new Set(prev)
    if (n.has(k)) n.delete(k); else n.add(k)
    return n
  })

  const cols = useMemo(() => {
    const ordersBy = (st: string, sub: (o: PurchaseOrderWithItems) => string): KCard[] =>
      orders.filter(o => o.status === st).map(o => ({
        id: o.id, title: o.suppliers?.name ?? 'Fornecedor', sub: sub(o), value: fmtBRL(o.total_value),
      }))

    const analisar: KCard[] = rows
      .filter(r => r.kanban_status === 'Analisar' && !r.active_order_number && ACTIONABLE.has(r.priority) && r.total_suggestion > 0)
      .map(r => ({ id: r.id, title: r.name, sub: `${r.priority} · ${r.sku}`, value: `${fmtInt(r.total_suggestion)} un` }))
    const aprovado: KCard[] = rows
      .filter(r => r.kanban_status === 'Compra Aprovada')
      .map(r => ({ id: r.id, title: r.name, sub: r.suppliers?.name ?? 'Sem fornecedor', value: `${fmtInt(r.approved_qty || r.total_suggestion)} un` }))
    const emitido = ordersBy('Pendente', o => `${o.purchase_order_items?.length ?? 0} itens`)
    const transito = ordersBy('Enviado', o => `ETA ${fmtDate(o.estimated_arrival)}`)
    const recebido = orders
      .filter(o => o.status === 'Recebido')
      .sort((a, b) => (b.received_at ?? '').localeCompare(a.received_at ?? ''))
      .slice(0, 20)
      .map(o => ({ id: o.id, title: o.suppliers?.name ?? 'Fornecedor', sub: `receb. ${fmtDate(o.received_at)}`, value: fmtBRL(o.total_value) }))

    return { analisar, aprovado, emitido, transito, recebido } as Record<string, KCard[]>
  }, [rows, orders])

  return (
    <div className="space-y-2.5">
      <p className="text-[11px] text-ink-faint px-0.5">Esteira do kanban — toque numa etapa para ver os itens. Pedidos abrem em Pedidos.</p>
      {COLS.map(col => {
        const cards = cols[col.key] ?? []
        const isOpen = open.has(col.key)
        const onCard = col.kind === 'order' ? () => navigate('/historico-pedidos') : undefined
        return (
          <div key={col.key} className="rounded-2xl border border-line bg-surface overflow-hidden">
            {/* Cabeçalho tocável da etapa */}
            <button
              onClick={() => toggle(col.key)}
              aria-expanded={isOpen}
              className="w-full flex items-center gap-2.5 px-3.5 py-3 active:bg-sunken/40 touch-action-manip"
            >
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: col.accent }} />
              <span className="text-sm font-bold uppercase tracking-wide" style={{ color: col.accent }}>{col.label}</span>
              <span className="text-[11px] font-semibold text-ink-muted bg-sunken border border-line rounded-full min-w-[22px] text-center px-1.5">{cards.length}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`ml-auto w-4 h-4 text-ink-faint transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Lista da etapa */}
            {isOpen && (
              <div className="px-3 pb-3 pt-2 space-y-2 border-t border-line max-h-[60vh] overflow-y-auto mobile-scroll">
                {cards.length === 0 ? (
                  <p className="text-[12px] text-ink-faint py-3 text-center">Nenhum item nesta etapa.</p>
                ) : cards.map(c => {
                  const inner = (
                    <>
                      <p className="text-[12px] font-semibold text-ink leading-snug">{c.title}</p>
                      <div className="flex items-center justify-between mt-1.5 gap-2">
                        <span className="text-[10.5px] text-ink-faint truncate">{c.sub}</span>
                        <span className="text-[10.5px] text-ink font-semibold tabular-nums shrink-0">{c.value}</span>
                      </div>
                    </>
                  )
                  return onCard
                    ? <button key={c.id} onClick={onCard} className="w-full text-left bg-sunken/40 border border-line rounded-xl p-2.5 active:bg-sunken transition-colors touch-action-manip">{inner}</button>
                    : <div key={c.id} className="bg-sunken/40 border border-line rounded-xl p-2.5">{inner}</div>
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
